import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../api/client'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { formatNumber } from '../lib/utils'

type CallbackStatus = 'processing' | 'success' | 'error'

export function PaymentCallbackPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [status, setStatus] = useState<CallbackStatus>('processing')
  const [message, setMessage] = useState('')
  const [pointsCharged, setPointsCharged] = useState(0)

  useEffect(() => {
    const confirmPayment = async () => {
      const paymentKey = searchParams.get('payment_key') || searchParams.get('pg_token')
      const orderId = searchParams.get('order_id') || searchParams.get('orderId')

      if (!paymentKey || !orderId) {
        setStatus('error')
        setMessage('결제 정보가 올바르지 않습니다')
        return
      }

      try {
        const result = await apiClient.confirmCharge({
          payment_key: paymentKey,
          order_id: orderId,
        })

        setStatus('success')
        setPointsCharged(result.pointsCharged)
        setMessage(t('payments.success'))
      } catch (error) {
        setStatus('error')
        setMessage(error instanceof Error ? error.message : t('payments.failed_msg'))
      }
    }

    confirmPayment()
  }, [searchParams, t])

  const handleNavigate = () => {
    if (status === 'success') {
      navigate('/points')
    } else {
      navigate('/payments')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-light-bg dark:bg-dark-bg px-4">
      <div className="w-full max-w-md">
        <div className="card p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
              <h2 className="text-xl font-semibold mt-6">{t('payments.processing')}</h2>
              <p className="text-light-text-muted dark:text-dark-text-muted mt-2">
                결제를 확인하고 있습니다...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 mx-auto text-success" />
              <h2 className="text-xl font-semibold mt-6">{message}</h2>
              <div className="mt-4 p-4 bg-success/10 rounded-lg">
                <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                  충전된 포인트
                </p>
                <p className="text-3xl font-bold text-success mt-1">
                  +{formatNumber(pointsCharged)}P
                </p>
              </div>
              <button
                onClick={handleNavigate}
                className="btn btn-primary w-full mt-6"
              >
                포인트 확인하기
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 mx-auto text-danger" />
              <h2 className="text-xl font-semibold mt-6">결제 실패</h2>
              <p className="text-light-text-muted dark:text-dark-text-muted mt-2">
                {message}
              </p>
              <button
                onClick={handleNavigate}
                className="btn btn-secondary w-full mt-6"
              >
                다시 시도하기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
